import type { SlideStyleType as GqlSlideStyleType } from "@/generated/graphql";
import { useSlideStyleMutations } from "@/lib/hooks/slides/editor/useSlideStyleMutations";
import { useSlideScreenshot } from "@/lib/hooks/slides/useSlideScreenshot";
import { useAppSelector } from "@/store/hooks";
import type { SlideStyleType } from "@ai-teach/types";
import { Button, Modal } from "@ai-teach/ui-web";
import { useState } from "react";
import toast from "react-hot-toast";
import { useSlideEditorContext } from "../../editor/SlideEditorContext";

interface SaveSlideStyleModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: SlideStyleType;
}

export function SaveSlideStyleModal({ isOpen, onClose, type }: SaveSlideStyleModalProps) {
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");

  const { slide } = useAppSelector((state) => state.slideEditor.present);
  const { canvasRef } = useSlideEditorContext();

  const { checkNameExists, createSlideStyle, createStyleLoading } = useSlideStyleMutations();
  const { captureScreenshotAsFile } = useSlideScreenshot(slide?.id ?? "");

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);

    // Clear error when user starts typing
    if (nameError) {
      setNameError("");
    }

    // Only validate length locally
    if (value.length > 100) {
      setNameError("Name must be 100 characters or less");
    }
  };

  const handleSave = async () => {
    // Basic validation
    if (!name.trim()) {
      setNameError("Style name is required");
      return;
    }

    if (name.length > 100) {
      setNameError("Name must be 100 characters or less");
      return;
    }

    if (!slide || !slide.elements || slide.elements.length === 0) {
      toast.error("No elements to save");
      return;
    }

    toast.loading("Saving Style...", { id: "saveStyleToast" });

    try {
      // Check if name exists in database
      const { data } = await checkNameExists({
        variables: { name: name.trim() },
      });

      if (data?.checkSlideStyleNameExists) {
        setNameError("A style with this name already exists");
        return;
      }

      const screenshotFile = await captureScreenshotAsFile(canvasRef, slide.id);

      // Name is unique, proceed with save
      await createSlideStyle({
        variables: {
          name: name.trim(),
          elements: slide.elements,
          type: type as GqlSlideStyleType,
          screenshotFile,
        },
      });

      toast.success("Slide style saved successfully", { id: "saveStyleToast" });
      setName("");
      setNameError("");
      onClose();
    } catch (error) {
      console.error("Error saving slide style:", error);
      toast.error("Failed to save slide style", { id: "saveStyleToast" });
    }
  };

  const handleClose = () => {
    setName("");
    setNameError("");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Save Slide Style">
      <div className="space-y-4 p-4">
        <div>
          <label htmlFor="style-name" className="block text-sm font-medium text-slate-700 mb-2">
            Style Name
          </label>
          <input
            id="style-name"
            type="text"
            value={name}
            onChange={handleNameChange}
            placeholder="Enter a unique name for this style"
            className={`w-full bg-white border rounded-md px-3 py-2 text-sm text-slate-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              nameError ? "border-error-500" : "border-gray-300"
            }`}
            disabled={createStyleLoading}
          />
          {nameError && <p className="mt-1 text-sm text-error-500">{nameError}</p>}
        </div>

        {slide?.elements && (
          <div className="text-xs text-slate-500">
            {slide.elements.length} element{slide.elements.length !== 1 ? "s" : ""} will be saved
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="ghost" onClick={handleClose} disabled={createStyleLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={createStyleLoading || !name.trim()}
          >
            {createStyleLoading ? "Saving..." : "Save Style"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
